/**
 * Care Services — consolidated category roadmap.
 *
 * 2026-06-08 UX refinement (per user request, see /app/memory/PRD.md):
 *   Replaces 12 individual placeholder cards with 5 consolidated category
 *   cards + 3 forward-looking roadmap cards. Each card may surface 2–3
 *   sub-feature pills so the user sees the full scope of what's bundled
 *   into the category without the page feeling like a sprawl of similar
 *   services. Goal is investor-grade clarity, not feature inventory.
 *
 * Page structure:
 *   1. "Healthcare Services" (5 cards, Coming Soon)
 *      - Specialist Consultation     (chat · video · appointments)
 *      - Pharmacy Support            (guidance · locator · refills)
 *      - Mental Wellness Support
 *      - Wellness Guidance           (OTC · next-step navigation)
 *      - Healthy Food Network        (delivery · farms · partners)
 *   2. "Roadmap" (3 cards, Future Feature)
 *      - Family Wellness Plans
 *      - Wellness Marketplace
 *      - Wellness+ Membership
 *
 * Mobile-first; responsive grid (1 / 2 / 3 cols at sm/lg). i18n keys live
 * under `careServices.*` in en.js — non-English locales fall back to
 * English via the LanguageContext until the next QA pass.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  ArrowLeft,
  UserCheck,
  Pill,
  Brain,
  Compass,
  Salad,
  Users,
  ShoppingBag,
  Crown,
  Sparkles,
  Stethoscope,
} from 'lucide-react';
import BottomNav from '@/components/BottomNav';
import AppFooter from '@/components/AppFooter';

const ACCENT = {
  purple: 'from-purple-100 to-fuchsia-100 text-purple-700',
  indigo: 'from-indigo-100 to-violet-100 text-indigo-700',
  teal:   'from-teal-100 to-emerald-100 text-teal-700',
  rose:   'from-rose-100 to-pink-100 text-rose-700',
  amber:  'from-amber-100 to-orange-100 text-amber-700',
};

const CategoryCard = ({ icon: Icon, title, description, features, accent, testid, badgeLabel }) => (
  <Card
    className="border border-gray-100 rounded-2xl shadow-sm bg-white opacity-95 cursor-not-allowed select-none transition-shadow hover:shadow-md"
    aria-disabled="true"
    data-testid={testid}
  >
    <CardContent className="p-4 h-full flex flex-col">
      <div className="flex items-center justify-between mb-2.5">
        <span className={`inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br ${ACCENT[accent]} shadow-sm`}>
          <Icon className="h-5 w-5" />
        </span>
        <Badge className="bg-gray-100 text-gray-500 text-[10px] px-2 py-0.5 border border-gray-200 font-medium tracking-wide">
          {badgeLabel}
        </Badge>
      </div>
      <p className="text-sm font-semibold text-gray-900 leading-tight">{title}</p>
      <p className="text-xs text-gray-500 mt-1 leading-relaxed">{description}</p>
      {features && features.length > 0 && (
        <ul
          className="mt-3 flex flex-wrap gap-1.5"
          data-testid={`${testid}-features`}
        >
          {features.map((feat) => (
            <li
              key={feat}
              className="inline-flex items-center text-[11px] font-medium text-gray-600 bg-gray-50 border border-gray-100 rounded-full px-2 py-0.5"
            >
              {feat}
            </li>
          ))}
        </ul>
      )}
    </CardContent>
  </Card>
);

const SectionGroup = ({ icon: Icon, title, subtitle, items, testid, badgeLabel }) => (
  <section className="mb-8" data-testid={testid}>
    <div className="flex items-center gap-2 mb-1">
      <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-100 to-violet-50 text-indigo-700">
        <Icon className="h-4 w-4" />
      </span>
      <h2 className="text-base font-semibold text-gray-900 leading-tight">{title}</h2>
    </div>
    {subtitle && <p className="text-xs text-gray-500 mb-3 ml-9">{subtitle}</p>}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {items.map((it) => (
        <CategoryCard
          key={it.testid}
          icon={it.icon}
          title={it.title}
          description={it.description}
          features={it.features}
          accent={it.accent}
          testid={it.testid}
          badgeLabel={badgeLabel}
        />
      ))}
    </div>
  </section>
);

const CareServices = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const comingSoonLabel = t('supportDeep.comingSoon');
  const futureFeatureLabel = t('careServices.futureFeatureBadge');

  // Section A — Healthcare Services (5 consolidated category cards).
  const healthcareItems = [
    {
      icon: Stethoscope,
      title: t('careServices.specialistConsultationTitle'),
      description: t('careServices.specialistConsultationDesc'),
      features: [
        t('careServices.specialistConsultationFeature1'),
        t('careServices.specialistConsultationFeature2'),
        t('careServices.specialistConsultationFeature3'),
      ],
      accent: 'purple',
      testid: 'care-specialist-consultation',
    },
    {
      icon: Pill,
      title: t('careServices.pharmacySupportTitle'),
      description: t('careServices.pharmacySupportDesc'),
      features: [
        t('careServices.pharmacySupportFeature1'),
        t('careServices.pharmacySupportFeature2'),
        t('careServices.pharmacySupportFeature3'),
      ],
      accent: 'indigo',
      testid: 'care-pharmacy-support',
    },
    {
      icon: Brain,
      title: t('careServices.mentalWellnessTitle'),
      description: t('careServices.mentalWellnessDesc'),
      accent: 'rose',
      testid: 'care-mental-wellness',
    },
    {
      icon: Compass,
      title: t('careServices.wellnessGuidanceTitle'),
      description: t('careServices.wellnessGuidanceDesc'),
      features: [
        t('careServices.wellnessGuidanceFeature1'),
        t('careServices.wellnessGuidanceFeature2'),
      ],
      accent: 'teal',
      testid: 'care-wellness-guidance',
    },
    {
      icon: Salad,
      title: t('careServices.healthyFoodNetworkTitle'),
      description: t('careServices.healthyFoodNetworkDesc'),
      features: [
        t('careServices.healthyFoodNetworkFeature1'),
        t('careServices.healthyFoodNetworkFeature2'),
        t('careServices.healthyFoodNetworkFeature3'),
      ],
      accent: 'teal',
      testid: 'care-healthy-food-network',
    },
  ];

  // Section B — Roadmap (3 forward-looking platform-growth pillars).
  // "Advanced Specialist Care", "Pharmacy Partnerships" and "Vendor
  // Collaboration Systems" were removed in this pass — they either
  // duplicate the categories above or describe internal operational
  // infrastructure (not user-facing services).
  const roadmapItems = [
    {
      icon: Users,
      title: t('careServices.futureFamilyWellnessTitle'),
      description: t('careServices.futureFamilyWellnessDesc'),
      accent: 'rose',
      testid: 'care-roadmap-family-wellness',
    },
    {
      icon: ShoppingBag,
      title: t('careServices.futureWellnessMarketplaceTitle'),
      description: t('careServices.futureWellnessMarketplaceDesc'),
      accent: 'amber',
      testid: 'care-roadmap-wellness-marketplace',
    },
    {
      icon: Crown,
      title: t('careServices.futureWellnessPlusTitle'),
      description: t('careServices.futureWellnessPlusDesc'),
      accent: 'amber',
      testid: 'care-roadmap-wellness-plus',
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 md:top-16 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex items-center gap-3">
          <button
            onClick={() => navigate('/dashboard')}
            aria-label="Back"
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            data-testid="care-services-back-btn"
          >
            <ArrowLeft className="h-4 w-4 text-gray-600" />
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg sm:text-xl font-bold text-gray-900 leading-tight">
              {t('careServices.pageTitle')}
            </h1>
            <p className="text-[11px] sm:text-xs text-gray-500">
              {t('careServices.pageSubtitle')}
            </p>
          </div>
          <Badge className="bg-gray-100 text-gray-600 text-[10px] px-2 py-0.5 border border-gray-200 font-medium hidden sm:inline-flex">
            {comingSoonLabel}
          </Badge>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="care-services-page">
        <SectionGroup
          icon={UserCheck}
          title={t('careServices.healthcareServicesTitle')}
          subtitle={t('careServices.healthcareServicesSubtitle')}
          items={healthcareItems}
          testid="care-section-healthcare-services"
          badgeLabel={comingSoonLabel}
        />

        <SectionGroup
          icon={Sparkles}
          title={t('careServices.roadmapTitle')}
          subtitle={t('careServices.roadmapSubtitle')}
          items={roadmapItems}
          testid="care-section-roadmap"
          badgeLabel={futureFeatureLabel}
        />

        <AppFooter />
      </main>

      <BottomNav />
    </div>
  );
};

export default CareServices;
